import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import dayjs from 'dayjs';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { BannerItem, useGetBannerApi } from '@/api/home';
import { useGetInfoApi } from '@/api/notice';
import homeSampleBanner from '@/assets/img/banner/home_sample_banner.png';
import ci1 from '@/assets/img/ci/1.svg';
import ci2 from '@/assets/img/ci/2.svg';
import ci3 from '@/assets/img/ci/3.svg';
import ci4 from '@/assets/img/ci/4.svg';
import ci5 from '@/assets/img/ci/5.svg';
import ci6 from '@/assets/img/ci/6.svg';
import ci7 from '@/assets/img/ci/7.svg';
import ci8 from '@/assets/img/ci/8.svg';
import ci9 from '@/assets/img/ci/9.svg';
import ci10 from '@/assets/img/ci/10.svg';
import ci11 from '@/assets/img/ci/11.svg';
import ci12 from '@/assets/img/ci/12.svg';
import ci13 from '@/assets/img/ci/13.svg';
import ci14 from '@/assets/img/ci/14.svg';
import ci15 from '@/assets/img/ci/15.svg';
import ci16 from '@/assets/img/ci/16.svg';
import ci17 from '@/assets/img/ci/17.svg';
import ci18 from '@/assets/img/ci/18.svg';
import ci19 from '@/assets/img/ci/19.svg';
import ci20 from '@/assets/img/ci/20.svg';
import ci21 from '@/assets/img/ci/21.svg';
import ci22 from '@/assets/img/ci/22.svg';
import ci23 from '@/assets/img/ci/23.svg';
import ci24 from '@/assets/img/ci/24.svg';
import ci25 from '@/assets/img/ci/25.svg';
import ci26 from '@/assets/img/ci/26.svg';
import ci27 from '@/assets/img/ci/27.svg';
import conferenceImg from '@/assets/img/conferenceImg.jpg';
import ArrowRightIcon from '@/assets/img/icon/arrowRightIcon.svg';
import ContactIcon from '@/assets/img/icon/contactIcon.svg';
import GotoIcon from '@/assets/img/icon/gotoIcon.svg';
import InformationIcon from '@/assets/img/icon/informationIcon.svg';
import IntroduceIcon from '@/assets/img/icon/introduceIcon.svg';
import PublicationIcon from '@/assets/img/icon/publicationIcon.svg';
import RegisterIcon from '@/assets/img/icon/registerIcon.svg';
import SocietyIcon from '@/assets/img/icon/societyIcon.svg';
import logo1 from '@/assets/img/logo/logo1.png';
import logo2 from '@/assets/img/logo/logo2.png';
import logo3 from '@/assets/img/logo/logo3.png';
import logo4 from '@/assets/img/logo/logo4.png';
import logo5 from '@/assets/img/logo/logo5.png';
import logo6 from '@/assets/img/logo/logo6.png';
import logo7 from '@/assets/img/logo/logo7.png';
import logo8 from '@/assets/img/logo/logo8.png';
import logo9 from '@/assets/img/logo/logo9.png';
import { CardContainer } from '@/components/common/Card/CardContainer/style';
import CardNotice from '@/components/common/Card/CardNotice/CardNotice';
import useIsMobile from '@/hooks/useIsMobile';
import * as S from '@/pages/Home/style';
import { convertInfoValue } from '@/utils/convertValue';

const logos = [
	{ logo: logo1, href: 'http://journal.scsk.or.kr/' },
	{ logo: logo4, href: 'https://kcia.or.kr' },
	{ logo: logo5, href: 'https://www.kcii.re.kr' },
	{ logo: logo6, href: 'https://ifscc2025.com/' },
	{ logo: logo7, href: 'https://www.kofst.or.kr/' },
	{ logo: logo8, href: 'http://www.ifscc2026.com', padding: true },
	{ logo: logo9, href: 'https://ifscc.org', division: 'width' },
	{ logo: logo2, href: 'https://www.mfds.go.kr', padding: true },
	{ logo: logo3, href: 'https://www.nts.go.kr', padding: true },
];
const ciLogos = [
	ci1,
	ci2,
	ci3,
	ci4,
	ci5,
	ci27,
	ci6,
	ci7,
	ci8,
	ci9,
	ci10,
	ci11,
	ci12,
	ci26,
	ci13,
	ci14,
	ci15,
	ci16,
	ci17,
	ci18,
	ci19,
	ci20,
	ci21,
	ci22,
	ci23,
	ci24,
	ci25,
];

const fallbackBanners: BannerItem[] = [
	{
		file_pk: 0,
		banner_enrollment_date: '',
		banner_pk: 0,
		file_path: homeSampleBanner,
		banner_title: '한국응용과학기술학회',
		banner_content: '연구와 교류의 중심',
	},
];

const Home = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const isMobile = useIsMobile(1100);
	const [activeTab, setActiveTab] = useState('공지사항');
	const { data: bannerData, isLoading: isBannerLoading } = useGetBannerApi({
		page: 1,
		row: 5,
	});
	const { data: infoData } = useGetInfoApi({
		page: 1,
		type: convertInfoValue(activeTab),
		row: 5,
	});
	const getDynamicField = (g: any, field: string) => {
		const type = convertInfoValue(activeTab);
		return g[`${type}_${field}`] || ''; // 동적으로 필드 처리
	};
	const bannerSlides = isBannerLoading ? [] : bannerData?.banner?.length ? bannerData.banner : fallbackBanners;
	const renderBannerText = (text?: string | null) =>
		t(text ?? '')
			.split('\n')
			.map((line, index, array) => (
				<span key={`${line}-${index}`}>
					{line}
					{index < array.length - 1 && <br />}
				</span>
			));
	return (
		<S.MainWrapper>
			<div className="container">
				<S.MainBanner>
					{bannerSlides.length > 0 && (
						<Swiper
							modules={[Navigation, Pagination, Autoplay]}
							slidesPerView={1}
							spaceBetween={0}
							navigation
							pagination={{
								clickable: true,
								el: '.swiper-pagination_custom .swiper-pagination',
							}}
							autoplay={{
								delay: 4000,
								disableOnInteraction: false,
							}}
							loop={true}
							style={{ width: '100%', height: '100%' }}
						>
							{bannerSlides.map((banner) => (
								<SwiperSlide className="first" key={`${banner.banner_pk}-${banner.file_pk}`}>
									<div className="img_container first">
										<img src={banner.file_path} alt={`mainBanner-${banner.banner_pk}`} className="background" />
									</div>
									<div className="textBoxWrapper">
										<div className="textBox">
											<h1 className="title">{renderBannerText(banner.banner_title)}</h1>
											<p className="description">{renderBannerText(banner.banner_content)}</p>
										</div>
									</div>
								</SwiperSlide>
							))}
							<div className="swiper-pagination_custom">
								<div className="swiper-pagination" />
							</div>
						</Swiper>
					)}
				</S.MainBanner>
			</div>
			<S.Description>
				<S.SectionEyebrow>{t('common.brand.acronym')}</S.SectionEyebrow>
				<div className="main_description_title">
					<h2 className="description_title">
						{t('한국응용과학기술학회,')} <br style={isMobile ? {} : { display: 'none' }} />
						{t('연구와 교류의 중심')}
					</h2>
					<p className="description_sub_title">
						{t('응용과학기술 산업의 미래를 이을 한국응용과학기술학회에서 최신 연구를 접하고')}
						<br style={isMobile ? {} : { display: 'none' }} /> {t('전문가들과 소통하세요.')}
					</p>
				</div>
				<S.CardList>
					<CardContainer onClick={() => navigate('/introduce')}>
						<S.CardWrapper>
							<S.CardTitle>
								{t('학회소개')}
								<img className="card-arrow" src={ArrowRightIcon} alt="" />
							</S.CardTitle>
							<S.CardSubTitle>
								{t('응용과학기술 연구 발전과 학술 교류를 위한 학회의')}
								<br style={isMobile ? { display: 'none' } : {}} />
								{t('역사와 비전을 소개합니다.')}
							</S.CardSubTitle>
						</S.CardWrapper>
						<S.ImgBoxWrapper>
							<S.ImgBox>
								<img src={IntroduceIcon} alt="introduce" />
							</S.ImgBox>
						</S.ImgBoxWrapper>
					</CardContainer>
					<CardContainer onClick={() => navigate('/society', { state: { menu: '학술행사 안내', depthMenu: '등록안내' } })}>
						<S.CardWrapper>
							<S.CardTitle>
								{t('학회활동')}
								<img className="card-arrow" src={ArrowRightIcon} alt="" />
							</S.CardTitle>
							<S.CardSubTitle>
								{t('학회활동을 통해 최신 연구 동향을')}
								<br style={isMobile ? { display: 'none' } : {}} />
								{t('공유하고 연구자들과 교류하세요.')}
							</S.CardSubTitle>
						</S.CardWrapper>
						<S.ImgBoxWrapper>
							<S.ImgBox>
								<img src={SocietyIcon} alt="society" />
							</S.ImgBox>
						</S.ImgBoxWrapper>
					</CardContainer>
					<CardContainer onClick={() => navigate('/auth/signup')}>
						<S.CardWrapper>
							<S.CardTitle>
								{t('회원안내')}
								<img className="card-arrow" src={ArrowRightIcon} alt="" />
							</S.CardTitle>
							<S.CardSubTitle>
								{t('학술대회 참여, 연구 자료 이용, 전문가')}
								<br style={isMobile ? { display: 'none' } : {}} />
								{t('네트워크 등의 혜택을 제공합니다.')}
							</S.CardSubTitle>
						</S.CardWrapper>
						<S.ImgBoxWrapper>
							<S.ImgBox>
								<img src={RegisterIcon} alt="register" />
							</S.ImgBox>
						</S.ImgBoxWrapper>
					</CardContainer>
					<CardContainer onClick={() => navigate('/publication')}>
						<S.CardWrapper>
							<S.CardTitle>
								{t('간행물')}
								<img className="card-arrow" src={ArrowRightIcon} alt="" />
							</S.CardTitle>
							<S.CardSubTitle>
								{t('회원들의 많은 훌륭한 논문의 게재된 학술지')}
								<br style={isMobile ? { display: 'none' } : {}} />
								{t('를 확인해보세요.')}
							</S.CardSubTitle>
						</S.CardWrapper>
						<S.ImgBoxWrapper>
							<S.ImgBox>
								<img src={PublicationIcon} alt="publication" />
							</S.ImgBox>
						</S.ImgBoxWrapper>
					</CardContainer>
					<CardContainer onClick={() => navigate('/information')}>
						<S.CardWrapper>
							<S.CardTitle>
								{t('자료실')}
								<img className="card-arrow" src={ArrowRightIcon} alt="" />
							</S.CardTitle>
							<S.CardSubTitle>
								{t('최신 연구 자료, 학술 논문, 뉴스 및 학회 소식')}
								<br style={isMobile ? { display: 'none' } : {}} /> {t('등 다양한 정보를 확인할 수 있습니다.')}
							</S.CardSubTitle>
						</S.CardWrapper>
						<S.ImgBoxWrapper>
							<S.ImgBox>
								<img src={InformationIcon} alt="introduce" />
							</S.ImgBox>
						</S.ImgBoxWrapper>
					</CardContainer>
					<CardContainer className="dark" onClick={() => navigate('/contact')}>
						<S.CardWrapper>
							<S.CardTitle>
								{t('문의하기')}
								<img className="card-arrow" src={ArrowRightIcon} alt="" />
							</S.CardTitle>
							<S.CardSubTitle>
								{t('한국응용과학기술학회에 대한 질문과 기타 문')}
								<br style={isMobile ? { display: 'none' } : {}} />
								{t('의사항에 대한 답변을 받을수 있습니다.')}
							</S.CardSubTitle>
						</S.CardWrapper>
						<S.ImgBoxWrapper>
							<S.ImgBox>
								<img src={ContactIcon} alt="contact" />
							</S.ImgBox>
						</S.ImgBoxWrapper>
					</CardContainer>
				</S.CardList>
			</S.Description>
			{/* <S.Description>
				<S.SpecialTitle>특별후원사</S.SpecialTitle>
				<S.LogoGrid className={isMobile ? 'mobile' : ''}>
					{ciLogos.map((logo, idx) => (
						<S.LogoImgWrapper className={isMobile ? 'mobile' : ''} key={idx}>
							<S.LogoImg className={isMobile ? 'mobile' : ''} src={logo} alt={`CI ${idx + 1}`} />
						</S.LogoImgWrapper>
					))}
				</S.LogoGrid>
			</S.Description> */}
			<S.Notice>
				<S.NoticeContent>
					<S.NoticeTabList>
						<S.Wrapper>
							<S.ButtonList>
								<S.TabButton isActive={activeTab === '공지사항'} onClick={() => setActiveTab('공지사항')}>
									{t('공지사항')}
								</S.TabButton>
								<S.Divider />
								<S.TabButton isActive={activeTab === '자유게시판'} onClick={() => setActiveTab('자유게시판')}>
									{t('자유게시판')}
								</S.TabButton>
							</S.ButtonList>
							<S.GotoButton type="button" onClick={() => navigate('/information', { state: { menu: activeTab } })}>
								{t('바로가기')}
								<img src={GotoIcon} />
							</S.GotoButton>
						</S.Wrapper>
					</S.NoticeTabList>
					<S.NoticeContentList>
						<CardNotice>
							{infoData && (infoData[convertInfoValue(activeTab)] ?? []).length >= 1 && (
								<S.NoticeList>
									{(infoData[convertInfoValue(activeTab)] ?? []).map((g, i) => (
										<S.NoticeListItem key={i} onClick={() => navigate(`/information/notice/${getDynamicField(g, 'pk')}`, { state: { menu: activeTab } })}>
											<div className="title">{getDynamicField(g, 'title')}</div>
											<div className="sub_title">{dayjs(getDynamicField(g, 'enrollment_date')).format('YYYY-MM-DD')}</div>
										</S.NoticeListItem>
									))}
								</S.NoticeList>
							)}
							{(!infoData || (infoData[convertInfoValue(activeTab)] ?? [])?.length < 1) && (
								<S.NoticeEmptyBox>
									<S.NoData>{t('해당 데이터가 없습니다.')}</S.NoData>
								</S.NoticeEmptyBox>
							)}
						</CardNotice>
					</S.NoticeContentList>
				</S.NoticeContent>
			</S.Notice>
			{/* <S.Company>
				<Swiper
					modules={[Navigation, Pagination, Autoplay]}
					slidesPerView={'auto'}
					spaceBetween={16}
					navigation
					pagination={{
						clickable: true,
						el: '.swiper-pagination-custom',
					}}
					// autoplay={{
					// 	delay: 4000,
					// 	disableOnInteraction: false,
					// }}
					loop={true}
					style={{ width: '100%', height: '100%' }}
				>
					{logos.map((logo, index) => (
						<SwiperSlide className="company_item" key={index}>
							<S.CompanyCard onClick={() => window.open(logo.href)} padding={logo.padding} division={logo.division as 'width' | 'height' | undefined}>
								<img src={logo.logo} alt="introduce" />
							</S.CompanyCard>
						</SwiperSlide>
					))}
				</Swiper>
			</S.Company> */}
		</S.MainWrapper>
	);
};

export default Home;
